import { forwardRef } from 'react'
import { TextInput, View, type TextInputProps } from 'react-native'
import { cva, type VariantProps } from 'class-variance-authority'

import { isWeb } from '@/constants/app'
import { semantic } from '@/constants/colors'
import { cn } from '@/utils/cn'

const viewVariants = cva('h-11 rounded-lg border border-transparent bg-fill-subtle', {
  variants: {
    error: {
      true: 'border-danger'
    },
    disabled: {
      true: 'opacity-30'
    }
  },
  defaultVariants: {
    error: false,
    disabled: false
  }
})

type BaseInputProps = Omit<TextInputProps, 'onChange' | 'onChangeText'>

export interface InputProps extends BaseInputProps, VariantProps<typeof viewVariants> {
  leftElement?: React.ReactElement
  rightElement?: React.ReactElement
  onChange?: (value: string) => void
  isTextarea?: boolean
}

export const Input = forwardRef<TextInput, InputProps>(
  (
    {
      className,
      leftElement,
      rightElement,
      error,
      disabled,
      isTextarea,
      onChange,
      editable,
      accessibilityState,
      ...props
    },
    ref
  ) => {
    return (
      <View
        className={cn(
          'flex-row items-center',
          viewVariants({ error, disabled }),
          isTextarea && 'h-[84px] bg-transparent'
        )}
      >
        {leftElement && <View className={'h-full flex-row items-center'}>{leftElement}</View>}
        <TextInput
          {...props}
          ref={ref}
          className={cn(
            'min-w-0 flex-1 self-stretch px-3 text-text-primary',
            isWeb && 'placeholder:text-text-quaternary',
            isTextarea && 'rounded-lg border-b-[32px] border-b-transparent bg-fill-subtle pt-3',
            error && 'outline-danger',
            className
          )}
          accessibilityState={{ ...accessibilityState, disabled: Boolean(disabled) || accessibilityState?.disabled }}
          editable={disabled ? false : editable}
          placeholderTextColor={semantic.text.quaternary}
          onChangeText={onChange}
        />
        {rightElement && (
          <View className={'h-full flex-row items-center gap-x-2 pr-3'} pointerEvents={disabled ? 'none' : 'auto'}>
            {rightElement}
          </View>
        )}
      </View>
    )
  }
)
Input.displayName = 'Input'
